import axios, { type InternalAxiosRequestConfig } from 'axios'

import { BACKEND_API_URL, CACHE_HALF_FULL_URL, CACHE_HALF_FULL_URL_TESTNET } from '@src/configs/env'
import { apiLogger as logger } from '@src/utils/logger'

// Example flags for mocking
export const MOCK_QUOTE_500 = false
export const MOCK_QUOTE_400 = false
export const MOCK_QUOTE_MALFORMED_DATA = false

export const MOCK_GAS_TOKEN_PRICES_500 = false
export const MOCK_GET_ALL_TOKENS_FROM_DB_500 = false
export const MOCK_GET_ALL_TOKENS_FROM_DB_200_NO_TOKENS = false

export const MOCK_ORDER_400 = false
export const MOCK_ORDER_200 = false

// Mock responses for testing
const mockResponses = new Map<
    string,
    {
        status: number
        data?: any
        headers?: Record<string, string>
        statusText?: string
    }
>()

// Function to set mock responses
const setMockResponse = (
    url: string,
    method: string,
    response: {
        status: number
        data?: any
        headers?: Record<string, string>
        statusText?: string
    }
) => {
    const key = `${method.toUpperCase()} ${url}`
    mockResponses.set(key, response)
}

// Conditionally set mock responses
if (MOCK_QUOTE_500) {
    setMockResponse('/v1/quotes', 'POST', {
        status: 500,
        headers: { 'X-Error-Code': '500' },
        statusText: 'MOCKED Internal Server Error',
    })
}

if (MOCK_QUOTE_400) {
    setMockResponse('/v1/quotes', 'POST', {
        status: 400,
        headers: { 'X-Error-Code': '400' },
        statusText: 'MOCKED Bad Request',
    })
}

if (MOCK_QUOTE_MALFORMED_DATA) {
    setMockResponse('/v1/quotes', 'POST', {
        status: 200,
        data: { srcAmount: `bad-amount - ${Math.random()}` },
        headers: {},
    })
}

if (MOCK_ORDER_400) {
    setMockResponse('/v1/orders', 'POST', {
        status: 400,
        headers: { 'X-Error-Code': '400' },
        statusText: 'MOCKED Bad request',
    })
}

if (MOCK_ORDER_200) {
    setMockResponse('/v1/orders', 'POST', {
        status: 200,
        data: { reject_order: false },
        headers: { 'X-Custom-Header': 'CustomValue' },
    })
}

// Conditionally set mock responses
if (MOCK_GAS_TOKEN_PRICES_500) {
    setMockResponse('/gas_token_prices', 'POST', {
        status: 500,
        headers: { 'X-Error-Code': '500' },
        statusText: 'MOCKED Internal Server Error',
    })
}

// Conditionally set mock responses
if (MOCK_GET_ALL_TOKENS_FROM_DB_500) {
    setMockResponse('/v1/tokens/get_all_tokens_from_db', 'POST', {
        status: 500,
        headers: { 'X-Error-Code': '500' },
        statusText: 'MOCKED Internal Server Error',
    })
}

// Conditionally set mock responses
if (MOCK_GET_ALL_TOKENS_FROM_DB_200_NO_TOKENS) {
    setMockResponse('/v1/tokens/get_all_tokens_from_db', 'POST', {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
        data: [],
    })
}

// Create base API instances
export const api = axios.create({ baseURL: BACKEND_API_URL })

api.interceptors.request.use(function (config: InternalAxiosRequestConfig) {
    return config
})

export const cacheHalfFullApi = axios.create({
    baseURL: CACHE_HALF_FULL_URL,
})

// Add request logging
cacheHalfFullApi.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        logger.http(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
            headers: config.headers,
            params: config.params,
            data: config.data,
        })

        const key = `${config.method?.toUpperCase()} ${config.url}`
        if (mockResponses.has(key)) {
            const mockResponse = mockResponses.get(key)
            if (mockResponse) {
                // Create a simulated response
                config.adapter = async () => ({
                    data: mockResponse.data,
                    status: mockResponse.status,
                    statusText: mockResponse.statusText || (mockResponse.status === 200 ? 'OK' : 'Error'),
                    headers: mockResponse.headers || {},
                    config,
                })
            }
        }
        return config
    },
    error => {
        logger.error('Request error', { error: error.message })
        return Promise.reject(error)
    }
)

// Add response logging
cacheHalfFullApi.interceptors.response.use(
    response => {
        logger.http(`Response: ${response.status} ${response.config.url}`, {
            data: response.data,
        })
        return response
    },
    error => {
        logger.error('Response error', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
        })
        return Promise.reject(error)
    }
)

export const cacheHalfFullTestnetApi = axios.create({ baseURL: CACHE_HALF_FULL_URL_TESTNET })

cacheHalfFullTestnetApi.interceptors.response.use(
    response => {
        logger.http(`Response: ${response.status} ${response.config.url}`, {
            data: response.data,
        })
        return response
    },
    error => {
        logger.error('Response error', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
        })
        return Promise.reject(error)
    }
)
